import { useEmbedContext } from '@thoughtspot/blink-context';
/* eslint-disable jsx-a11y/anchor-is-valid */
import cx from 'classnames';
import _ from 'lodash';
import React, { Children, useEffect } from 'react';
import { getOnEnter } from '../_utils';
import { BaseLink } from '../link/base-link';
import { TabNavigation } from './tab-navigation';
import { scrollTabIntoView, useTab } from './tab-util';
/**
 * Positional contract — arguments must be passed in this exact order.
 * `tabPanelClassName` onward are optional and may be omitted entirely, or
 * passed as `undefined` placeholders to reach a later one. Several are the
 * same `string | undefined` / `boolean | undefined` shape, so TypeScript
 * will not catch a call site that gets the order wrong.
 *
 * @param children React children to render inside the active tab panel.
 * @param selectedTabId Id of the currently active tab.
 * @param styles CSS module styles object for the tab layout.
 * @param headerClass Class name applied to the tab header container.
 * @param tabPanelClassName Optional class name for the tab panel.
 * @param hideTabHeader Whether to hide the tab header row entirely.
 * @param enableOverflowControls Whether to render scroll/overflow controls.
 * @param forceUpdateTabs Forces a tab re-render when true.
 * @param ariaLabel Accessible name for the tablist container (9th argument).
 *
 * TODO: migrate callers to a named options object instead of positional
 * args, so TypeScript can catch order mistakes here.
 */
const BaseTab = (children, selectedTabId, styles, headerClass, tabPanelClassName, hideTabHeader, enableOverflowControls, forceUpdateTabs = false, ariaLabel) => {
    const embedContext = useEmbedContext();
    const { embedParams, onLayoutChange } = embedContext;
    const isFullHeight = embedParams === null || embedParams === void 0 ? void 0 : embedParams.isFullHeightPinboard;
    const { selectTab, selectedTab } = useTab(selectedTabId, children);
    useEffect(() => {
        if (forceUpdateTabs) {
            selectTab(selectedTabId, false);
        }
    }, [forceUpdateTabs, selectTab, selectedTabId]);
    useEffect(() => {
        if (isFullHeight) {
            onLayoutChange();
        }
    }, [selectedTab, children]);
    const tabHeader = ({ name, id, tooltipText = '', disabled = false, customHeader = null, onTabItemClick = _.noop, tabBottomBorder = false, tabSwitchable = true, }) => {
        const tabHeaderClass = cx(styles.headerItem, {
            [styles.headerItemSelected]: selectedTab === id,
            [styles.disabledTab]: disabled,
            [styles.borderBottom]: tabBottomBorder,
        });
        const handleClick = (event, tabId, isTabDisabled, isTabClicked) => {
            event.stopPropagation();
            if (isTabClicked) {
                selectTab(tabId, isTabDisabled);
                if (enableOverflowControls) {
                    scrollTabIntoView(tabId);
                }
            }
            if (!isTabDisabled) {
                onTabItemClick(event);
            }
        };
        return (React.createElement("li", { className: cx(tabHeaderClass, {
                [styles.headerNavItem]: enableOverflowControls,
            }), "data-tooltip-content": tooltipText, role: "tab", "aria-selected": selectedTab === id, key: id, tabIndex: 0, id: id, "data-testid": `tab-header-item-${id}`, "data-ts-element": "tab", onClick: (event) => handleClick(event, id, disabled, tabSwitchable), onKeyDown: getOnEnter((event) => handleClick(event, id, disabled, tabSwitchable)) },
            React.createElement(BaseLink, { className: cx(styles.headerText), "aria-controls": `section-${id}`, "aria-selected": selectedTab === id }, customHeader || name)));
    };
    const renderTabContent = () => React.Children.toArray(children).map((tabItem) => {
        /** reassigned children to child as children available on upper scope
         * to remove linting error
         */
        const { id, children: child } = tabItem.props;
        const tabContentClass = cx(tabPanelClassName, styles.tabPanel);
        return (React.createElement("section", { className: tabContentClass, "aria-hidden": !(selectedTab === id), key: id, id: `section-${id}`, "aria-labelledby": id }, child));
    });
    const renderTabItems = () => Children.toArray(children).map((tabItem) => tabHeader(tabItem.props));
    return (React.createElement(React.Fragment, null,
        !hideTabHeader &&
            (enableOverflowControls ? (React.createElement(TabNavigation, { headerClass: headerClass, ariaLabel: ariaLabel }, renderTabItems())) : (React.createElement("ul", { className: headerClass, role: "tablist", "aria-label": ariaLabel }, renderTabItems()))),
        renderTabContent()));
};
export { BaseTab };
//# sourceMappingURL=base-tab.js.map